import React, { useMemo, useState, useCallback } from "react";
import { useFormParentContext } from "@/shared/contexts/FormParent";
import { useAppLayerContext } from "@/shared/contexts/AppLayer";
import { CoverageParameterControls } from "@/shared/components/CoverageEditor/CoverageParameterControls";
import { CoverageTypeControl, CoverageLevel } from "@/shared/components/CoverageEditor/CoverageTypeControl";
import { CoverageUtils, CoverageValue } from "@/shared/utils/CoverageUtils";
import { Quote, CoverageSettings } from "@/shared/types/Quote.interface";
import { DefaultPlanConfig } from "@/shared/hooks/useDefaultPlans";
import { ResponsiveModalSheet } from "../ResponsiveModalSheet";
import { Button } from "../ui";
import Strings from "@/shared/utils/Strings.constants";
import { UIUtils } from "@/shared/utils/UIUtils";
import { cn } from "@/shared/utils";

/**
 * NOTE:
 * This modal intentionally DOES NOT use PetModal.
 * PetModal is Add-Pet specific and does not accept children.
 */

interface EditCoverageModalProps {
    isOpen: boolean;
    onClose: () => void;
    defaultPlanConfig?: DefaultPlanConfig;
}

export function EditCoverageModal({ isOpen, onClose, defaultPlanConfig }: EditCoverageModalProps) {
    const { getValues, setValue } = useFormParentContext<Quote>();
    const { appState, updateAppState } = useAppLayerContext();

    const { activePetIndex = 0, petPlansObject, isAnnualBilling, isMultiPetLinked, isQuoteUpdating, updateQuote } = appState;

    const quote = getValues();
    const policies = useMemo(() => quote?.policies ?? [], [quote?.policies]);
    const activePet = policies?.[activePetIndex];

    // Discounts
    const discounts = activePet?.discounts ?? [];
    const hasDiscount = discounts.length > 0;
    const totalDiscount = hasDiscount ? discounts.map(discount => discount.amount ?? 0).reduce((prev, curr) => (prev ?? 0) + (curr ?? 0), 0) : 0;

    // Pricing
    const formattedDetails = activePet ? CoverageUtils.formatPolicyDetails(activePet) : undefined;

    const basePrice = formattedDetails?.basePrice?.value ?? 0;

    const petPlans = useMemo(() => petPlansObject?.petPlans ?? {}, [petPlansObject?.petPlans]);
    const activePetPlans = activePet?.id ? petPlans[activePet.id] : undefined;

    /**
     * ---------------------------
     * Draft Coverage State
     * ---------------------------
     */
    const [draftCoverage, setDraftCoverage] = useState<CoverageSettings | null>(activePet?.coverageSettings ?? null);

    // The draft diverges locally (every parameter handler writes it), so it stays state. It is
    // re-seeded from the quote whenever the modal opens or the active pet changes — React's "adjusting
    // state when a prop changes" pattern, keyed on both inputs the effect used to watch.
    const [prevOpenPet, setPrevOpenPet] = useState({ isOpen, activePetIndex });
    if (isOpen !== prevOpenPet.isOpen || activePetIndex !== prevOpenPet.activePetIndex) {
        setPrevOpenPet({ isOpen, activePetIndex });
        if (isOpen) {
            const pet = getValues()?.policies?.[activePetIndex];
            if (pet?.coverageSettings) {
                setDraftCoverage(pet.coverageSettings);
            }
        }
    }

    /**
     * Coverage options (same as CoverageEditor)
     */
    const allCoverageOptions = useMemo(() => {
        if (!activePetPlans?.length) return null;
        return CoverageUtils.formatCustomCoverageData(activePetPlans, isAnnualBilling);
    }, [activePetPlans, isAnnualBilling]);

    const coverageTypes = allCoverageOptions?.coverageTypes;

    /**
     * CoverageValue wrapper required by CoverageParameterControls
     */
    const activePetCoverage = useMemo<CoverageValue | undefined>(() => (draftCoverage ? { name: "custom", config: draftCoverage } : undefined), [draftCoverage]);

    /**
     * Current coverage level
     */
    const coverageLevel: CoverageLevel = useMemo(() => {
        const hasIllness = draftCoverage?.coverages?.some(c => c.type === "illness");
        return hasIllness ? "accidentAndIllness" : "accidentOnly";
    }, [draftCoverage]);

    /**
     * ---------------------------
     * Parameter Handlers
     * ---------------------------
     */
    const handlers = useMemo(
        () => ({
            onAnnualLimitChange: (value: string) =>
                setDraftCoverage(prev =>
                    prev
                        ? {
                              ...prev,
                              amounts: {
                                  ...prev.amounts,
                                  annualLimit: Number(value)
                              }
                          }
                        : prev
                ),

            onReimbursementChange: (value: string) =>
                setDraftCoverage(prev =>
                    prev
                        ? {
                              ...prev,
                              amounts: {
                                  ...prev.amounts,
                                  reimbursementRate: Number(value)
                              }
                          }
                        : prev
                ),

            onDeductibleChange: (value: string) =>
                setDraftCoverage(prev =>
                    prev
                        ? {
                              ...prev,
                              amounts: {
                                  ...prev.amounts,
                                  annualDeductible: Number(value)
                              }
                          }
                        : prev
                )
        }),
        []
    );

    /**
     * Coverage Type Toggle
     */
    // Add these alongside your existing useMemos:

    const coverageValuesToMatch = useMemo(() => {
        if (!allCoverageOptions) return [];
        const valuesWithMultipleOptions = CoverageUtils.findKeysWithMultipleOptions(allCoverageOptions);
        return CoverageUtils.convertKeysToMatchCriteria(valuesWithMultipleOptions);
    }, [allCoverageOptions]);

    const wellnessValue = useMemo(() => {
        const wellnessCoverage = draftCoverage?.coverages?.find(c => c.type?.includes("wellness"))?.type;
        if (wellnessCoverage === "wellness-gold" || wellnessCoverage === "wellness-platinum") return wellnessCoverage;
        return "None";
    }, [draftCoverage]);

    const handleCoverageLevelChange = useCallback(
        (level: CoverageLevel) => {
            if (!activePetPlans?.length) return;

            const currentCoverages = [...(draftCoverage?.coverages ?? [])];
            let newCoverages = [...currentCoverages];

            if (level === "accidentAndIllness" && !currentCoverages.some(c => c.type === "illness")) {
                newCoverages.push({ type: "illness", id: "tempId", name: "Illness Coverage" });
            } else if (level === "accidentOnly") {
                newCoverages = newCoverages.filter(c => c.type !== "illness");
            }

            const updatedSettings: CoverageSettings = {
                ...draftCoverage,
                coverages: newCoverages
            };

            const matchingPlan = CoverageUtils.getPlanDataFromCoverageSettings(activePetPlans, updatedSettings, coverageValuesToMatch);

            if (matchingPlan) {
                const newSettings = CoverageUtils.getCoverageSettingsFromPlanData(matchingPlan, wellnessValue === "None" ? undefined : wellnessValue);
                setDraftCoverage(newSettings);
            }
        },
        [draftCoverage, activePetPlans, coverageValuesToMatch, wellnessValue]
    );

    const activePetId = activePet?.id;

    const draftPriceData = useMemo(() => {
        if (!draftCoverage || !activePetId || !activePetPlans?.length) return null;

        return CoverageUtils.calculatePriceData({
            coverageSettings: draftCoverage,
            policies,
            isMultiPetLinked: false,
            activePetId,
            petPlans: petPlans,
            coverageValuesToMatch,
            isAnnualBilling,
            transactionFee: 0,
            feeType: "per-quote"
        });
    }, [draftCoverage, activePetId, activePetPlans, policies, petPlans, coverageValuesToMatch, isAnnualBilling]);

    const petPrice = draftPriceData?.perPetPriceData?.[activePetId ?? ""];
    const planTotal = petPrice?.preDiscountPrice ?? Number(basePrice);
    const planTotalAdjusted = petPrice?.finalPrice ?? planTotal + totalDiscount;
    /**
     * Settings object expected by CoverageParameterControls
     */

    const settings = useMemo(() => {
        if (!draftCoverage || !activePet) return undefined;

        return {
            annualLimitValue: draftCoverage.amounts?.annualLimit?.toString(),
            reimbursementValue: draftCoverage.amounts?.reimbursementRate?.toString(),
            annualDeductibleValue: draftCoverage.amounts?.annualDeductible?.toString(),
            activePet,
            allPets: policies,
            floatingVariant: "floating-inner" as const,
            activePetCoverage,
            defaultPlanConfig: defaultPlanConfig ?? {},
            isMultiPetLinked
        };
    }, [draftCoverage, activePet, policies, activePetCoverage, defaultPlanConfig, isMultiPetLinked]);

    /**
     * Save Coverage
     */
    const handleSave = useCallback(async () => {
        if (!draftCoverage || !activePet || !updateQuote) return;

        const currentValues = getValues();
        const updatedPolicies = [...(currentValues.policies ?? [])];

        updatedPolicies[activePetIndex] = {
            ...updatedPolicies[activePetIndex],
            coverageSettings: draftCoverage
        };

        const updatedQuoteData = {
            ...currentValues,
            policies: updatedPolicies
        };

        try {
            const updatedQuote = await updateQuote.mutateAsync({
                quote: updatedQuoteData,
                updateType: "UPDATE"
            });

            // break apply-to-all link
            updateAppState({ isMultiPetLinked: false });
            setValue("extra.isMultiPetLinked", false);

            // Reset parent form with recalculated backend values
            setValue("policies", updatedQuote.policies, {
                shouldDirty: false
            });

            onClose();
        } catch (error) {
            console.error("Failed to update coverage:", error);
            updateAppState({ hasUnknownError: true });
        }
    }, [draftCoverage, activePet, updateQuote, getValues, activePetIndex, setValue, onClose, updateAppState]);

    if (!isOpen || !activePet || !settings) return null;

    return (
        <ResponsiveModalSheet
            isOpen={isOpen}
            onClose={onClose}
            title={
                <div className="flex w-full items-start justify-between gap-4 text-left">
                    {/* LEFT SIDE */}
                    <div className="min-w-0 flex-1 text-left">
                        <span className="block truncate text-lg font-bold text-content-primary">
                            Customize <br /> {activePet?.name}&apos;s coverage
                        </span>
                    </div>

                    {/* RIGHT SIDE */}
                    <div className="flex shrink-0 flex-col items-end">
                        <div className={`py-1 ${hasDiscount ? "bg-background-success" : "bg-background-brand-b-secondary"} rounded-lg px-2`}>
                            <div className="flex flex-col items-end">
                                <span className={cn("whitespace-nowrap text-lg font-bold leading-7 text-content-primary", hasDiscount && "text-content-success")}>
                                    {UIUtils.formatCurrency(planTotalAdjusted)}
                                    <span className="text-sm font-medium leading-5">/{isAnnualBilling ? "yr" : "mo"}</span>
                                </span>
                                {hasDiscount && (
                                    <span className="text-right text-sm font-medium leading-5 text-content-secondary line-through">{UIUtils.formatCurrency(planTotal)}</span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            }
            sheetSide="top"
            showCloseButton
            styles={{
                sheet: {
                    content: "flex flex-col",
                    title: "flex justify-between items-center"
                },
                modal: {
                    title: "flex justify-between items-center",
                    content: "flex flex-col !max-w-[360px]",
                    closeButton: "hidden"
                }
            }}
        >
            <div data-testid="edit-coverage-modal" className="flex flex-col gap-6">
                <CoverageTypeControl
                    coverageTypes={coverageTypes}
                    coverageLevel={coverageLevel}
                    onCoverageLevelChange={handleCoverageLevelChange}
                    floatingVariant="floating-inner"
                />

                <CoverageParameterControls settings={settings} handlers={handlers} view="desktop" />

                <Button loading={isQuoteUpdating} variant="default-rounded" onClick={handleSave} className="w-full">
                    {Strings.SAVE}
                </Button>
            </div>
        </ResponsiveModalSheet>
    );
}
